// lib/i18n/useT.ts
"use client";

import { useTranslations as useNextIntlTranslations } from "next-intl";
import { useMemo } from "react";

const ALIAS = "@:";

function isAliasString(v: unknown): v is string {
  return typeof v === "string" && v.trim().startsWith(ALIAS);
}

// Resolve a possibly-relative alias like "@:.foo.bar"
const resolvePath = (path: string, ns?: string) => {
  const raw = path.slice(ALIAS.length).trim(); // remove "@:"
  if (raw.startsWith(".") && ns) return `${ns}${raw}`; // relative to ns
  return raw; // absolute path
};

export function useT(ns?: string) {
  // Namespaced and root translators
  const tNs = useNextIntlTranslations(ns);
  const tRoot = useNextIntlTranslations();

  // ---- Public API ----
  const t = useMemo(() => {
    // Follow alias chains to a final **key** (for strings), or return a **value** for objects
    const followAlias = (
      key: string,
      seen = new Set<string>()
    ): { finalKey?: string; value?: unknown } => {
      const raw = tNs.raw(key); // look in namespace first
      if (isAliasString(raw)) {
        const nextKey = resolvePath(raw, ns);
        if (seen.has(nextKey)) return { value: "[i18n:cycle]" }; // guard cycles
        seen.add(nextKey);
        const nextRaw = tRoot.raw(nextKey);
        if (isAliasString(nextRaw)) {
          return followAlias(nextKey, seen); // keep walking
        }
        // If the referenced value is an object, we return it via value; if it's a string, prefer key for formatting
        if (typeof nextRaw === "object" && nextRaw !== null)
          return { value: nextRaw };
        return { finalKey: nextKey };
      }
      // Not an alias at this level
      return {};
    };

    // Deeply resolve alias strings inside objects/arrays
    const deepResolve = (val: unknown, seen = new Set<string>()): unknown => {
      if (isAliasString(val)) {
        const target = resolvePath(val, ns);
        if (seen.has(target)) return "[i18n:cycle]";
        seen.add(target);
        const next = tRoot.raw(target);
        return deepResolve(next, seen);
      }
      if (Array.isArray(val))
        return val.map((v) => deepResolve(v, new Set(seen)));
      if (val && typeof val === "object") {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(val))
          out[k] = deepResolve(v, new Set(seen));
        return out;
      }
      return val;
    };

    const fn = (key: string, values?: Record<string, unknown>) => {
      try {
        const { finalKey } = followAlias(key);
        // Ensure values are of the correct type for next-intl
        const castValues = values as
          | Record<string, string | number | Date>
          | undefined;
        // If alias resolves to another key, format using that key; else format original
        return finalKey ? tRoot(finalKey, castValues) : tNs(key, castValues);
      } catch (error) {
        // Return the key when a translation is missing
        console.warn(`Missing translation for key: ${key}`, error);
        return key;
      }
    };
    // Return raw data (objects/arrays) with aliases resolved
    fn.raw = (key: string) => {
      try {
        const { finalKey, value } = followAlias(key);
        const base = value ?? (finalKey ? tRoot.raw(finalKey) : tNs.raw(key));
        return deepResolve(base);
      } catch (error) {
        // Return the key when a translation is missing
        console.warn(`Missing translation for key: ${key}`, error);
        return key;
      }
    };
    // Re-expose .formatters if you use them
    fn.formatters = (tNs as unknown as { formatters: unknown }).formatters;
    return fn;
  }, [ns, tNs, tRoot]);

  return t;
}
